import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button } from "@/components/ui-kit";
import { Mail, KeyRound, Upload, X, ChevronLeft, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", address: "", npwp: "", photo_b64: "" });
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!user) return;
    const id = setTimeout(() => setForm({
      name: user.name || "",
      phone: user.phone || "",
      address: user.address || "",
      npwp: user.npwp || "",
      photo_b64: user.photo_b64 || "",
    }), 0);
    return () => clearTimeout(id);
  }, [user]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Maks 2 MB"); return; }
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, photo_b64: String(r.result || "").split(",")[1] || "" }));
    r.readAsDataURL(f);
  };

  const saveProfile = async () => {
    setBusy("profile");
    try {
      await api.patch("/auth/profile", form);
      toast.success("Profil tersimpan");
      refresh && refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(""); }
  };

  const requestEmailChange = async () => {
    setBusy("email");
    try {
      await api.post("/auth/email-change/request", emailForm);
      toast.success("Tautan konfirmasi dikirim ke email baru. Cek inbox Anda.");
      setEmailForm({ new_email: "", current_password: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim konfirmasi"); }
    finally { setBusy(""); }
  };

  const changePw = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    if (pwForm.new_password.length < 6) { toast.error("Min 6 karakter"); return; }
    setBusy("pw");
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Kata sandi berhasil diubah. Cek email Anda untuk notifikasi keamanan.");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal ubah password"); }
    finally { setBusy(""); }
  };

  const isClient = (user?.account_type || "").toLowerCase() === "klien";

  return (
    <div className="max-w-[900px] mx-auto p-4 md:p-6 lg:p-8 space-y-5">
      <Link to={isClient ? "/client" : "/dashboard"} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-stone-500 hover:text-terra-700">
        <ChevronLeft className="h-3 w-3" /> Beranda
      </Link>
      <PageHeader overline="Akun" title="Profil Saya" description="Kelola data akun, email, dan keamanan." />

      {/* Section: data umum */}
      <Card className="p-6" data-testid="profile-form">
        <h2 className="text-sm font-semibold text-stone-900 mb-4">Data Pribadi</h2>
        <div className="flex items-start gap-5 mb-5">
          {form.photo_b64 ? (
            <div className="relative h-20 w-20 border border-stone-200 overflow-hidden shrink-0">
              <img src={`data:image/png;base64,${form.photo_b64}`} alt="" className="h-full w-full object-cover" />
              <button onClick={() => setForm({ ...form, photo_b64: "" })} className="absolute top-0 right-0 bg-red-600 text-white p-0.5" data-testid="profile-photo-remove"><X className="h-3 w-3" /></button>
            </div>
          ) : (
            <div className="h-20 w-20 bg-gradient-to-br from-terra-500 to-emerald-600 text-white flex items-center justify-center font-display text-2xl font-bold border border-stone-200">
              {(form.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <label className="cursor-pointer text-xs uppercase tracking-wider border border-stone-300 hover:border-stone-500 px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="profile-photo-upload">
            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
            <Upload className="h-3 w-3" /> Ubah Foto
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nama"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></Field>
          <Field label="Email (read-only)"><Input value={user?.email || ""} disabled className="!bg-stone-50" /></Field>
          <Field label="No. Telepon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></Field>
          <Field label="NPWP"><Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} data-testid="profile-npwp" /></Field>
          <Field label="Alamat" className="md:col-span-2"><textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="inp" data-testid="profile-address" /></Field>
        </div>
        <div className="flex justify-end mt-4 pt-3 border-t border-stone-100">
          <Button onClick={saveProfile} disabled={busy === "profile"} data-testid="profile-save">{busy === "profile" ? "Menyimpan…" : "Simpan Profil"}</Button>
        </div>
      </Card>

      {/* Section: email change */}
      <Card className="p-6" data-testid="profile-email-change">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-terra-700" />
          <h2 className="text-sm font-semibold text-stone-900">Ubah Email</h2>
        </div>
        <p className="text-xs text-stone-500 mb-3">Tautan konfirmasi akan dikirim ke email baru. Email lama berlaku sampai Anda klik konfirmasi.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input type="email" placeholder="Email baru" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} data-testid="profile-new-email" />
          <Input type="password" placeholder="Kata sandi saat ini" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} data-testid="profile-email-pwd" />
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={requestEmailChange} disabled={busy === "email" || !emailForm.new_email || !emailForm.current_password} variant="secondary" data-testid="profile-email-submit">
            {busy === "email" ? "Mengirim…" : "Kirim Tautan Konfirmasi"}
          </Button>
        </div>
      </Card>

      {/* Section: password change */}
      <Card className="p-6" data-testid="profile-password-change">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4 text-terra-700" />
          <h2 className="text-sm font-semibold text-stone-900">Ubah Kata Sandi</h2>
        </div>
        <p className="text-xs text-stone-500 mb-3 flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-emerald-600" /> Notifikasi keamanan akan dikirim ke email setelah perubahan.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input type="password" placeholder="Kata sandi saat ini" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="profile-pw-current" />
          <Input type="password" placeholder="Kata sandi baru (min 6)" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="profile-pw-new" />
          <Input type="password" placeholder="Konfirmasi" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="profile-pw-confirm" />
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={changePw} disabled={busy === "pw"} variant="secondary" data-testid="profile-pw-submit">{busy === "pw" ? "Mengubah…" : "Ubah Kata Sandi"}</Button>
        </div>
      </Card>
      <style>{`.inp{ width:100%; border:1px solid #d6d3d1; padding:.55rem .75rem; font-size:.875rem; }
        .inp:focus{ border-color:#c54a34; outline: 1px solid #c54a34; }`}</style>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-wider text-stone-600 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} className={`inp ${props.className || ""}`} />;
}
